import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/admin/alerts")({
  head: () => ({ meta: [{ title: "Admin — Alert Emails" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: AlertsPage,
});

const ALERT_TEMPLATES = ["new-submission", "submission-digest"];

type LogRow = {
  id: string;
  message_id: string | null;
  template_name: string;
  recipient_email: string;
  status: string;
  error_message: string | null;
  created_at: string;
};

const STATUS_STYLES: Record<string, string> = {
  sent: "bg-green-100 text-green-800 border-green-700",
  pending: "bg-yellow-100 text-yellow-800 border-yellow-700",
  failed: "bg-red/10 text-red border-red",
  dlq: "bg-red/10 text-red border-red",
  bounced: "bg-red/10 text-red border-red",
  complained: "bg-red/10 text-red border-red",
  suppressed: "bg-navy-deep/10 text-navy-deep border-navy-deep",
};

function AlertsPage() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState<number>(50);
  const [templateFilter, setTemplateFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [recipientFilter, setRecipientFilter] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate({ to: "/auth" as any }); return; }
      const { data: roles } = await (supabase as any)
        .from("user_roles").select("role").eq("user_id", session.user.id);
      setIsAdmin(!!roles?.some((r: any) => r.role === "admin"));
      setAuthChecked(true);
    })();
  }, [navigate]);

  const [allRows, setAllRows] = useState<LogRow[]>([]);

  const load = async () => {
    setLoading(true); setError(null);
    const { data, error } = await (supabase as any)
      .from("email_send_log")
      .select("id, message_id, template_name, recipient_email, status, error_message, created_at")
      .in("template_name", ALERT_TEMPLATES)
      .order("created_at", { ascending: false })
      .limit(1000);
    if (error) { setError(error.message); setLoading(false); return; }
    const seen = new Set<string>();
    const deduped: LogRow[] = [];
    for (const r of (data ?? []) as LogRow[]) {
      const key = r.message_id ?? r.id;
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(r);
    }
    setAllRows(deduped);
    setLoading(false);
  };

  useEffect(() => {
    if (isAdmin) {
      load();
    }
  }, [isAdmin]);

  const normalizeStatus = (s: string) =>
    s === "dlq" || s === "failed" || s === "bounced" || s === "complained" ? "failed" : s;

  const recipientQuery = recipientFilter.trim().toLowerCase();
  const search = searchQuery.trim().toLowerCase();
  const filtered = allRows.filter((r) => {
    if (templateFilter !== "all" && r.template_name !== templateFilter) return false;
    if (statusFilter !== "all" && normalizeStatus(r.status) !== statusFilter) return false;
    if (recipientQuery && !r.recipient_email.toLowerCase().includes(recipientQuery)) return false;
    if (search) {
      const inMsgId = (r.message_id ?? "").toLowerCase().includes(search);
      const inError = (r.error_message ?? "").toLowerCase().includes(search);
      if (!inMsgId && !inError) return false;
    }
    return true;
  });
  const rows = filtered.slice(0, limit);

  if (!authChecked) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><p className="font-display text-sm uppercase text-navy-deep/70">Loading…</p></div>;
  }
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md text-center border-4 border-navy-deep bg-white p-8">
          <h1 className="font-display text-2xl font-bold uppercase text-navy-deep">No admin access</h1>
          <Link to="/admin" className="mt-4 inline-block text-xs uppercase tracking-wider text-navy-deep/70 hover:text-red">← Admin</Link>
        </div>
      </div>
    );
  }

  const counts = rows.reduce(
    (acc, r) => {
      const s = r.status === "dlq" || r.status === "failed" || r.status === "bounced" || r.status === "complained" ? "failed" : r.status;
      acc[s] = (acc[s] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b-4 border-navy-deep bg-white px-4 py-4 sm:px-8 flex flex-wrap gap-4 items-center justify-between">
        <div>
          <Link to="/admin" className="text-xs uppercase tracking-wider text-navy-deep/60 hover:text-red">← Admin</Link>
          <h1 className="font-display text-2xl font-bold uppercase text-navy-deep">Alert Emails</h1>
          <p className="text-xs text-navy-deep/60 mt-1">Submission alerts (immediate + daily digest), latest delivery status per email.</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <label className="font-display text-xs uppercase tracking-wider text-navy-deep/70">Show last</label>
          <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))}
            className="border-2 border-navy-deep bg-white px-2 py-1 text-sm">
            {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <button onClick={load} className="border-2 border-navy-deep bg-navy-deep px-3 py-1 font-display text-xs uppercase text-white hover:bg-red">
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </header>

      <div className="px-4 py-6 sm:px-8">
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 border-2 border-navy-deep bg-white p-4">
          <div>
            <label className="block font-display text-xs uppercase tracking-wider text-navy-deep/70 mb-1">Template</label>
            <select value={templateFilter} onChange={(e) => setTemplateFilter(e.target.value)}
              className="w-full border-2 border-navy-deep bg-white px-2 py-1 text-sm">
              <option value="all">All</option>
              <option value="new-submission">Immediate</option>
              <option value="submission-digest">Daily digest</option>
            </select>
          </div>
          <div>
            <label className="block font-display text-xs uppercase tracking-wider text-navy-deep/70 mb-1">Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border-2 border-navy-deep bg-white px-2 py-1 text-sm">
              <option value="all">All</option>
              <option value="sent">Sent</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="suppressed">Suppressed</option>
            </select>
          </div>
          <div className="lg:col-span-2">
            <label className="block font-display text-xs uppercase tracking-wider text-navy-deep/70 mb-1">Recipient email</label>
            <input type="text" value={recipientFilter} onChange={(e) => setRecipientFilter(e.target.value)}
              placeholder="contains…"
              className="w-full border-2 border-navy-deep bg-white px-2 py-1 text-sm" />
          </div>
          <div className="sm:col-span-2 lg:col-span-4">
            <label className="block font-display text-xs uppercase tracking-wider text-navy-deep/70 mb-1">Search (message ID or error text)</label>
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="paste a message_id or part of an error message…"
              className="w-full border-2 border-navy-deep bg-white px-2 py-1 text-sm font-mono" />
          </div>
          {(templateFilter !== "all" || statusFilter !== "all" || recipientFilter || searchQuery) && (
            <div className="sm:col-span-2 lg:col-span-4 flex items-center justify-between text-xs text-navy-deep/60">
              <span>Showing {rows.length} of {filtered.length} matching ({allRows.length} total loaded)</span>
              <button onClick={() => { setTemplateFilter("all"); setStatusFilter("all"); setRecipientFilter(""); setSearchQuery(""); }}
                className="font-display uppercase tracking-wider text-navy-deep hover:text-red">Clear filters</button>
            </div>
          )}
        </div>

        <div className="mb-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total", value: rows.length },
            { label: "Sent", value: counts.sent ?? 0 },
            { label: "Pending", value: counts.pending ?? 0 },
            { label: "Failed", value: counts.failed ?? 0 },
          ].map((s) => (
            <div key={s.label} className="border-2 border-navy-deep bg-white p-4">
              <div className="font-display text-xs uppercase tracking-wider text-navy-deep/60">{s.label}</div>
              <div className="font-display text-3xl font-bold text-navy-deep mt-1">{s.value}</div>
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-4 border-2 border-red bg-red/10 px-3 py-2 text-sm text-red">{error}</div>
        )}

        <div className="border-2 border-navy-deep bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy-deep text-white">
              <tr>
                <th className="text-left px-3 py-2 font-display text-xs uppercase tracking-wider">When</th>
                <th className="text-left px-3 py-2 font-display text-xs uppercase tracking-wider">Template</th>
                <th className="text-left px-3 py-2 font-display text-xs uppercase tracking-wider">Recipient</th>
                <th className="text-left px-3 py-2 font-display text-xs uppercase tracking-wider">Status</th>
                <th className="text-left px-3 py-2 font-display text-xs uppercase tracking-wider">Error</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-navy-deep/60 text-sm">No alert emails yet.</td></tr>
              )}
              {rows.map((r) => {
                const badgeClass = STATUS_STYLES[r.status] ?? "bg-navy-deep/10 text-navy-deep border-navy-deep";
                return (
                  <tr key={r.id} className="border-t border-navy-deep/10 align-top">
                    <td className="px-3 py-2 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2">{r.template_name === "new-submission" ? "Immediate" : r.template_name === "submission-digest" ? "Daily digest" : r.template_name}</td>
                    <td className="px-3 py-2">{r.recipient_email}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block border px-2 py-0.5 text-xs font-display uppercase tracking-wider ${badgeClass}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-red break-words max-w-[320px]">{r.error_message ?? ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
